import { clsx } from 'clsx';
import { useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Spinner,
  Text,
} from '@optiaxiom/react';

import type { AgentFileUrl } from '@frontend/types';

import { filePreviewStyle as style } from './FilePreview.css';

function isAgentFileUrl(file: File | AgentFileUrl): file is AgentFileUrl {
  return (
    typeof file === 'object' &&
    'type' in file &&
    typeof (file as AgentFileUrl).type === 'string' &&
    ('preview_url' in file || 'description' in file)
  );
}

function FilePreview({
  file,
  isOpen,
  setIsOpen,
}: {
  file: File | AgentFileUrl;
  isOpen: boolean;
  setIsOpen: (val: boolean) => void;
}) {
  const [isLoading, setIsLoading] = useState(true);

  const handleLoad = () => {
    setIsLoading(false);
  };

  const fileLink = useMemo(() => {
    if (file instanceof File) {
      return URL.createObjectURL(file);
    }
    return 'preview_url' in file ? file.preview_url : undefined;
  }, [file]);

  const isImage = useMemo(() => {
    return file.type?.startsWith('image/');
  }, [file]);

  // Helper to get display name/description
  const displayName = useMemo(() => {
    if ('name' in file && file.name) {
      return file.name;
    }
    if (isAgentFileUrl(file) && file.description) {
      return file.description;
    }
    return '';
  }, [file]);

  return (
    <Dialog onOpenChange={setIsOpen} open={isOpen}>
      <DialogContent
        className={clsx({
          [style.imageContainer]: isImage,
          [style.iframeContainer]: !isImage,
        })}
      >
        <DialogHeader>{displayName}</DialogHeader>
        <DialogBody>
          <div className={style.previewContainer}>
            {fileLink && isLoading && <Spinner />}
            {fileLink && isImage ? (
              <img
                src={fileLink}
                alt={
                  'name' in file && file.name
                    ? file.name
                    : isAgentFileUrl(file) && file.description
                      ? file.description
                      : 'File preview'
                }
                onLoad={handleLoad}
                className={clsx({
                  [style.imageLoading]: isLoading,
                  [style.imageLoaded]: !isLoading,
                })}
              />
            ) : (
              fileLink && (
                <iframe
                  src={fileLink}
                  width="100%"
                  height="756px"
                  onLoad={handleLoad}
                  className={clsx({
                    [style.iframeLoading]: isLoading,
                    [style.iframeLoaded]: !isLoading,
                  })}
                />
              )
            )}
            {!fileLink && <Text size="sm">Unable to load the file.</Text>}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button
            appearance="primary"
            onClick={() => {
              if (fileLink) {
                window.open(fileLink, '_blank');
              }
            }}
          >
            Download
          </Button>
          <Button appearance="default" onClick={() => setIsOpen(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export { FilePreview };
